import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { AnnouncementBar } from "@/components/layout/AnnouncementBar";
import { GuideBar } from "@/components/guide/GuideBar";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { CampaignOverlay } from "@/components/CampaignOverlay";
import { ADMIN_NAV_GROUPS } from "@/pages/admin/AdminLayout";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import {
  LayoutDashboard, Coins, Sparkles, UserRound, Compass, Users, Bell, Gem, HeartPulse,
  Store, CalendarCheck, Wallet, Clock, BarChart3, Handshake, FileText, ShieldCheck, Shield, Settings, Layers, ClipboardList, Megaphone, MessageSquareQuote, Flower2, ArrowLeftRight, Target, Wind, Rss, MessageCircle, Activity, Share2, Images, BookOpen, Timer, Infinity as InfinityIcon, Bookmark, Gift, ShoppingBag, MessagesSquare, Ban, Bot, Leaf, Trophy, Users2, Menu,
} from "lucide-react";

const VIEW_KEY = "mw_view_mode";

export function AppLayout() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [dmUnread, setDmUnread] = useState(0);
  const [mobileNav, setMobileNav] = useState(false);
  const [viewMode, setViewMode] = useState(() => (typeof localStorage !== "undefined" ? localStorage.getItem(VIEW_KEY) : null) || "coach");
  useEffect(() => {
    if (!user) return;
    api.get("/notifications?limit=1").then(({ data }) => setUnread(data?.unread_count || 0)).catch(() => {});
    api.get("/messages/unread-count").then(({ data }) => setDmUnread(data?.unread_count || 0)).catch(() => {});
  }, [user, location.pathname]);

  const notif = { to: "/app/notifications", key: "notifications", icon: Bell, badge: unread };
  const role = user?.role;

  // My Journey — personal experience navigation. Every existing route is
  // preserved; only regrouped for clarity (Home · Explore · Breathe · WEVA ·
  // Connect · My Space).
  const clientGroups = [
    { heading: "home", items: [
      { to: "/app", key: "dashboard", icon: LayoutDashboard, end: true },
      notif,
    ] },
    { heading: "explore", items: [
      { to: "/app/deep-within", key: "deep_within", icon: Flower2 },
      { to: "/app/practices", key: "practices", icon: Leaf },
      { to: "/app/journal", key: "journal", icon: FileText },
      { to: "/app/goals", key: "goals", icon: Target },
    ] },
    { heading: "breathe", items: [
      { to: "/app/breakout", key: "breakout", icon: Activity },
      { to: "/app/breathwork", key: "breathwork", icon: Wind },
      { to: "/app/connected-breath", key: "connected_breath", icon: InfinityIcon },
      { to: "/app/focus-timer", key: "focus_timer", icon: Timer },
    ] },
    { heading: "weva", items: [
      { to: "/app/weva", key: "weva", icon: Sparkles },
    ] },
    { heading: "connect", items: [
      { to: "/app/messages", key: "messages", icon: MessageCircle, badge: dmUnread },
      { to: "/app/circles", key: "circles", icon: Users },
      { to: "/app/feed", key: "feed", icon: Rss },
      { to: "/app/coach-hub", key: "coach_hub", icon: Handshake },
      { to: "/app/bookings", key: "my_bookings", icon: CalendarCheck },
      { to: "/app/collab-packages", key: "collab_packages", icon: Layers },
      { to: "/app/collaboration", key: "collaboration", icon: ShieldCheck },
      { to: "/app/collab", key: "collab_area", icon: MessagesSquare },
      { to: "/marketplace", key: "marketplace", icon: Compass },
    ] },
    { heading: "my_space", items: [
      { to: "/app/collections", key: "collections", icon: Bookmark },
      { to: "/app/story-wall", key: "story_wall", icon: Images },
      { to: "/app/story-mode", key: "story_mode", icon: BookOpen },
      { to: "/app/share-cards", key: "share_cards", icon: Share2 },
      { to: "/app/unlocked-methods", key: "unlocked_methods", icon: Gift },
      { to: "/app/unlocked-posts", key: "unlocked_posts", icon: ShoppingBag },
      { to: "/app/snapshot-requests", key: "snapshot_requests", icon: Sparkles },
      { to: "/app/blocked-users", key: "blocked_users", icon: Ban },
      { to: "/app/credits", key: "credits", icon: Coins },
      { to: "/app/membership", key: "membership", icon: Sparkles },
      { to: "/app/account", key: "account", icon: UserRound },
    ] },
  ];

  const isCoach = role === "coach";
  const coachInClientView = isCoach && viewMode === "client";

  const switchView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_KEY, mode); } catch { /* ignore */ }
    navigate(mode === "client" ? "/app/deep-within" : "/app");
  };

  let groups;
  if (isCoach && !coachInClientView) {
    // My Practice — professional workspace navigation. All existing coach
    // routes preserved; regrouped as Overview · Clients · Schedule & Plans ·
    // Create & Offer · Community · Business · Settings.
    groups = [
      { heading: "overview", items: [
        { to: "/app", key: "dashboard", icon: LayoutDashboard, end: true },
        { to: "/app/feed", key: "feed", icon: Rss },
        { to: "/app/messages", key: "messages", icon: MessageCircle, badge: dmUnread },
        notif,
      ] },
      { heading: "clients", items: [
        { to: "/app/coach/client-pulse", key: "pulse", icon: HeartPulse },
        { to: "/app/coach/recommendations", key: "recommendations", icon: MessageSquareQuote },
        { to: "/app/coach/client-wins", key: "client_wins", icon: Trophy },
      ] },
      { heading: "schedule", items: [
        { to: "/app/coach/bookings", key: "bookings", icon: CalendarCheck },
        { to: "/app/coach/availability", key: "availability", icon: Clock },
        { to: "/app/coach/plans", key: "plans", icon: ClipboardList },
      ] },
      { heading: "create", items: [
        { to: "/app/coach/offerings", key: "offerings", icon: Store },
        { to: "/app/coach/methods", key: "methods", icon: Layers },
        { to: "/app/coach/content", key: "content", icon: FileText },
      ] },
      { heading: "community", items: [
        { to: "/app/coach/circles", key: "circles", icon: Users },
        { to: "/app/coach/circles/inner", key: "inner", icon: Gem },
        { to: "/app/coach/circles/mein-kreis", key: "mein_kreis", icon: Sparkles },
        { to: "/app/coach/collectives", key: "collectives", icon: Users2 },
      ] },
      { heading: "business", items: [
        { to: "/app/coach/earnings", key: "earnings", icon: Wallet },
        { to: "/app/coach/analytics", key: "analytics", icon: BarChart3 },
        { to: "/app/coach/ads", key: "ads", icon: Megaphone },
        { to: "/app/coach/collab", key: "collab", icon: Handshake },
        { to: "/app/coach/assistant", key: "assistant", icon: Bot },
        { to: "/app/coach/referral", key: "referral", icon: Share2 },
      ] },
      { heading: "settings", items: [
        { to: "/app/coach/profile", key: "profile", icon: UserRound },
        { to: "/app/coach/verify", key: "verify", icon: ShieldCheck },
        { to: "/app/credits", key: "credits", icon: Coins },
        { to: "/app/membership", key: "membership", icon: Sparkles },
        { to: "/app/account", key: "account", icon: Settings },
        { to: "/marketplace", key: "marketplace", icon: Compass },
      ] },
    ];
  } else if (role === "admin") {
    const inAdmin = location.pathname.startsWith("/app/admin");
    groups = inAdmin
      ? [
          { heading: "start", items: [{ to: "/app", key: "dashboard", icon: LayoutDashboard, end: true }, notif] },
          ...ADMIN_NAV_GROUPS,
        ]
      : [
          { heading: "start", items: [{ to: "/app", key: "dashboard", icon: LayoutDashboard, end: true }, notif] },
          { heading: "account", items: [
            { to: "/app/admin", key: "admin", icon: Shield },
            { to: "/app/account", key: "account", icon: Settings },
            { to: "/marketplace", key: "marketplace", icon: Compass },
          ] },
        ];
  } else {
    groups = clientGroups;
  }

  const experience = role === "admin" ? null : (isCoach && !coachInClientView) ? "practice" : "journey";

  const navBody = (
    <>
      {experience && (
        <p className="px-3 font-display text-base font-semibold text-foreground" data-testid="nav-experience">
          {t(`nav.experience.${experience}`)}
        </p>
      )}
      {isCoach ? (
        <button
          onClick={() => { switchView(coachInClientView ? "coach" : "client"); setMobileNav(false); }}
          data-testid="view-switch-btn"
          className="flex w-full items-center gap-2 rounded-xl border border-dashed border-primary/50 bg-primary/5 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
        >
          <ArrowLeftRight className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate text-left">{coachInClientView ? t("nav.switch_to_coach") : t("nav.switch_to_client")}</span>
        </button>
      ) : null}
      {groups.map((g) => (
        <div key={g.heading}>
          <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{g.title || t(`nav.group.${g.heading}`)}</p>
          <div className="space-y-0.5">
            {g.items.map((it) => (
              <NavLink
                key={it.to + it.key}
                to={it.to}
                end={it.end}
                onClick={() => setMobileNav(false)}
                data-testid={`app-nav-${it.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`
                }
              >
                <it.icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{it.label || t(`nav.${it.key}`)}</span>
                {it.badge > 0 && (
                  <span data-testid="nav-notif-badge" className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-semibold text-white">{it.badge > 99 ? "99+" : it.badge}</span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className="flex min-h-screen flex-col bg-secondary/30">
      <AnnouncementBar />
      <Navbar />
      <ImpersonationBanner />
      <GuideBar />
      <CampaignOverlay />
      <div className="container-page flex-1 py-6">
        {/* Mobile section navigation */}
        <div className="mb-4 md:hidden">
          <Sheet open={mobileNav} onOpenChange={setMobileNav}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-nav-trigger"
                className="flex w-full items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-medium text-foreground shadow-sm"
              >
                <Menu className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-left">{experience ? t(`nav.experience.${experience}`) : t("nav.dashboard")}</span>
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 overflow-y-auto">
              <SheetTitle className="sr-only">{experience ? t(`nav.experience.${experience}`) : t("nav.dashboard")}</SheetTitle>
              <nav className="mt-6 space-y-5" data-testid="app-sidebar-mobile">{navBody}</nav>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-8">
          <aside className="hidden w-56 shrink-0 md:block">
            <nav className="sticky top-24 max-h-[calc(100vh-7rem)] space-y-5 overflow-y-auto pr-1" data-testid="app-sidebar">
              {navBody}
            </nav>
          </aside>
          <main className="min-w-0 flex-1 animate-fade-in">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
